import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Input, Button, DatePicker, InputNumber, message, Row, Col, Space, Modal } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import ApiService from '../services/api';

const UnifiedActions = () => {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [form] = Form.useForm();
  const [data, setData] = useState({ trips: [], fuel: [], expenses: [] });
  const [loading, setLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingTrips, setEditingTrips] = useState([]);
  const [editingFuel, setEditingFuel] = useState([]);
  const [editingExpenses, setEditingExpenses] = useState([]);
  const [newLocation, setNewLocation] = useState('');
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [tempAssignment, setTempAssignment] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    setEditingTrips(data.trips.map(t => ({ ...t })));
    setEditingFuel(data.fuel.map(f => ({ ...f })));
    setEditingExpenses(data.expenses.map(e => ({ ...e })));
  }, [data]);

  const loadInitialData = async () => {
    try {
      const dashboardData = await ApiService.getDashboardData();
      setDrivers(dashboardData.drivers || []);
      setTrucks(dashboardData.trucks || []);
      
      const locationsData = await ApiService.getLocations();
      setLocations(locationsData.locations || []);
    } catch (error) {
      message.error('Failed to load data');
    }
  };

  const handleDriverChange = (driverId) => {
    setSelectedDriver(driverId);
    form.setFieldsValue({ driver_id: driverId });
    if (selectedDate) {
      loadDriverDateData(driverId, selectedDate);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    form.setFieldsValue({ date });
    if (selectedDriver) {
      loadDriverDateData(selectedDriver, date);
      loadAssignment(selectedDriver, date);
    }
  };

  const loadAssignment = async (driverId, date) => {
    if (!driverId || !date) return;
    const dateString = date.format('YYYY-MM-DD');
    try {
      const result = await ApiService.getAssignment(driverId, dateString);
      if (result && result.length > 0) {
        setTempAssignment(result[0]);
      } else {
        setTempAssignment(null);
      }
    } catch (error) {
      setTempAssignment(null);
    }
  };

  const loadDriverDateData = async (driverId, date) => {
    if (!driverId || !date) return;
    
    setLoading(true);
    const dateString = date.format('YYYY-MM-DD');
    
    console.log('Loading data for driver:', driverId, 'date:', dateString);
    
    try {
      const [tripsData, fuelData, expensesData] = await Promise.all([
        ApiService.getTrips(driverId, dateString).catch((err) => {
          console.error('Trips API error:', err);
          return [];
        }),
        ApiService.getFuelRequests(driverId, dateString).catch((err) => {
          console.error('Fuel API error:', err);
          return [];
        }),
        ApiService.getExpenses(driverId, dateString).catch((err) => {
          console.error('Expenses API error:', err);
          return [];
        })
      ]);
      
      console.log('Raw API responses:', { tripsData, fuelData, expensesData });
      
      const trips = tripsData.length > 0 && tripsData[0]?.trips ? tripsData[0].trips : [];
      const fuel = fuelData || [];
      const expenses = expensesData || [];
      
      console.log('Parsed data:', { trips, fuel, expenses });
      
      setData({ trips, fuel, expenses });
      
      if (trips.length > 0 || fuel.length > 0 || expenses.length > 0) {
        message.info(`Loaded: ${trips.length} trips, ${fuel.length} fuel, ${expenses.length} expenses`);
      }
    } catch (error) {
      console.error('loadDriverDateData error:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (type, data) => {
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    
    try {
      if (type === 'trips' && data.length > 0) {
        await ApiService.createTrip({
          date,
          trips: data,
          admin_created: true,
          driver_id: driverId
        });
        message.success(`${data.length} trips saved`);
      }
      
      if (type === 'expenses' && data.length > 0) {
        console.log(`Saving ${data.length} expenses:`, data);
        const results = [];
        for (let i = 0; i < data.length; i++) {
          const expense = data[i];
          console.log(`Saving expense ${i + 1}:`, expense);
          try {
            const result = await ApiService.createExpense({
              driver_id: driverId,
              date,
              expense_type: expense.expense_type,
              amount: expense.amount,
              description: expense.description,
              admin_created: true
            });
            console.log(`Expense ${i + 1} saved:`, result);
            results.push(result);
          } catch (error) {
            console.error(`Failed to save expense ${i + 1}:`, error);
            message.error(`Failed to save expense ${i + 1}: ${error.message}`);
          }
        }
        message.success(`${results.length} of ${data.length} expenses saved`);
      }
      
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to save ${type}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addTrip = () => setEditingTrips([...editingTrips, { origin: '', destination: '', price: 0 }]);
  const addFuel = () => setEditingFuel([...editingFuel, { amount_spent: 0, liters: 0, odometer: 0 }]);
  const addExpense = () => setEditingExpenses([...editingExpenses, { expense_type: '', amount: 0, description: '' }]);

  const removeTrip = (index) => setEditingTrips(editingTrips.filter((_, i) => i !== index));
  const removeFuel = (index) => setEditingFuel(editingFuel.filter((_, i) => i !== index));
  const removeExpense = (index) => setEditingExpenses(editingExpenses.filter((_, i) => i !== index));

  const updateTrip = (index, field, value) => {
    const updated = [...editingTrips];
    updated[index] = { ...updated[index], [field]: value };
    setEditingTrips(updated);
  };

  const updateFuel = (index, field, value) => {
    const updated = [...editingFuel];
    updated[index] = { ...updated[index], [field]: value };
    setEditingFuel(updated);
  };

  const updateExpense = (index, field, value) => {
    const updated = [...editingExpenses];
    updated[index] = { ...updated[index], [field]: value };
    // Auto-populate description with expense type if description is empty
    if (field === 'expense_type' && !updated[index].description) {
      updated[index].description = value.charAt(0).toUpperCase() + value.slice(1);
    }
    setEditingExpenses(updated);
  };

  const saveTrips = () => {
    const validTrips = editingTrips.filter(t => t.origin && t.destination && t.price > 0);
    handleSubmit('trips', validTrips);
  };

  const clearTrips = async () => {
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    try {
      await ApiService.createTrip({ date, trips: [], admin_created: true, driver_id: driverId });
      message.success('All trips cleared');
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to clear trips: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearFuel = async () => {
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    try {
      const existingFuel = await ApiService.getFuelRequests(driverId, date);
      for (const fuel of existingFuel) {
        await ApiService.deleteFuel(fuel.driver_id, fuel.fuel_id);
      }
      message.success('All fuel records cleared');
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to clear fuel: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearExpenses = async () => {
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    try {
      const existingExpenses = await ApiService.getExpenses(driverId, date);
      for (const expense of existingExpenses) {
        await ApiService.deleteExpense(expense.driver_id, expense.expense_id);
      }
      message.success('All expenses cleared');
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to clear expenses: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveFuel = async () => {
    const validFuel = editingFuel.filter(f => f.amount_spent > 0 && f.liters > 0 && f.odometer > 0);
    if (validFuel.length === 0) {
      message.warning('No valid fuel records to save. Please fill all required fields.');
      return;
    }
    
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    
    try {
      // First, delete existing fuel records for this driver/date
      const existingFuel = await ApiService.getFuelRequests(driverId, date);
      for (const fuel of existingFuel) {
        await ApiService.deleteFuel(fuel.driver_id, fuel.fuel_id);
      }
      
      // Then save all new fuel records
      for (const fuelRecord of validFuel) {
        await ApiService.createFuelRequest({
          driver_id: driverId,
          date,
          amount_spent: fuelRecord.amount_spent,
          liters: fuelRecord.liters,
          odometer: fuelRecord.odometer,
          admin_created: true,
          status: 'approved'
        });
      }
      
      message.success(`${validFuel.length} fuel record(s) saved`);
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to save fuel: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveExpenses = async () => {
    const validExpenses = editingExpenses.filter(e => e.expense_type && e.amount > 0);
    if (validExpenses.length === 0) {
      message.warning('No valid expenses to save. Please fill expense type and amount.');
      return;
    }
    
    const formValues = form.getFieldsValue();
    const driverId = formValues.driver_id;
    const date = formValues.date?.format('YYYY-MM-DD');
    
    if (!driverId || !date) {
      message.error('Please select driver and date first');
      return;
    }
    
    setLoading(true);
    
    try {
      // First, delete existing expenses for this driver/date
      const existingExpenses = await ApiService.getExpenses(driverId, date);
      for (const expense of existingExpenses) {
        await ApiService.deleteExpense(expense.driver_id, expense.expense_id);
      }
      
      // Then save new expenses
      const results = [];
      for (let i = 0; i < validExpenses.length; i++) {
        const expense = validExpenses[i];
        try {
          const result = await ApiService.createExpense({
            driver_id: driverId,
            date,
            expense_type: expense.expense_type,
            amount: expense.amount,
            description: expense.description,
            admin_created: true
          });
          results.push(result);
        } catch (error) {
          console.error(`Failed to save expense ${i + 1}:`, error);
          message.error(`Failed to save expense ${i + 1}: ${error.message}`);
        }
      }
      
      message.success(`${results.length} expenses saved`);
      await loadDriverDateData(driverId, formValues.date);
    } catch (error) {
      message.error(`Failed to save expenses: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addLocation = async () => {
    if (!newLocation.trim()) {
      message.error('Please enter a location name');
      return;
    }
    
    try {
      await ApiService.addLocation(newLocation.trim());
      message.success('Location added successfully');
      setNewLocation('');
      setLocationModalVisible(false);
      
      // Reload locations
      const locationsData = await ApiService.getLocations();
      setLocations(locationsData.locations || []);
    } catch (error) {
      message.error('Failed to add location');
    }
  };

  const saveAssignment = async (values) => {
    try {
      await ApiService.setAssignment({
        driver_id: selectedDriver,
        date: selectedDate.format('YYYY-MM-DD'),
        truck_id: values.truck_id,
        reason: values.reason || 'temporary_switch'
      });
      message.success('Assignment saved');
      setAssignmentModalVisible(false);
      await loadAssignment(selectedDriver, selectedDate);
    } catch (error) {
      message.error('Failed to save assignment');
    }
  };

  const deleteAssignment = async () => {
    try {
      await ApiService.deleteAssignment(selectedDriver, selectedDate.format('YYYY-MM-DD'));
      message.success('Assignment deleted');
      setTempAssignment(null);
      setAssignmentModalVisible(false);
    } catch (error) {
      message.error('Failed to delete assignment');
    }
  };

  const renderDriverOption = (driver) => {
    const truck = trucks.find(t => t.truck_id === driver.truck_id);
    const truckDisplay = truck ? truck.license_plate : 'No Truck';
    return (
      <Select.Option key={driver.driver_id} value={driver.driver_id}>
        {driver.name} ({truckDisplay})
      </Select.Option>
    );
  };

  return (
    <div>
      <h1>Unified Actions</h1>
      <p>Select driver and date once, manage all data types together</p>
      
      <Card title="Select Driver & Date" style={{ maxWidth: 1200, marginBottom: 24 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="driver_id" label="Driver" rules={[{ required: true }]}>
                <Select placeholder="Select driver" onChange={handleDriverChange}>
                  {drivers.map(renderDriverOption)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} onChange={handleDateChange} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label=" ">
                <Space>
                  <Button type="primary" onClick={() => setLocationModalVisible(true)}>Add Location</Button>
                  <Button onClick={() => setAssignmentModalVisible(true)} disabled={!selectedDriver || !selectedDate}>
                    {tempAssignment ? 'Edit Assignment' : 'Set Temp Truck'}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {selectedDriver && selectedDate && (
        <>
          {tempAssignment && (
            <Card style={{ marginBottom: 16, background: '#fffbe6', borderColor: '#ffe58f' }}>
              <strong>Temporary Assignment:</strong> Driver using {trucks.find(t => t.truck_id === tempAssignment.truck_id)?.license_plate || tempAssignment.truck_id} on this date
            </Card>
          )}
          <Row gutter={24}>
          <Col span={8}>
            <Card 
              title={`Trips (${editingTrips.length})`} 
              size="small"
              extra={
                <Space>
                  <Button size="small" icon={<PlusOutlined />} onClick={addTrip}>Add</Button>
                  <Button size="small" type="primary" onClick={saveTrips} loading={loading}>Save</Button>
                </Space>
              }
            >
              {editingTrips.map((trip, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span>Trip {index + 1}</span>
                    <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeTrip(index)} />
                  </div>
                  <input
                    list={`unified-origin-options-${index}`}
                    placeholder="Type origin..."
                    value={trip.origin}
                    onChange={(e) => updateTrip(index, 'origin', e.target.value)}
                    style={{ width: '100%', marginBottom: 4, padding: '6px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}
                  />
                  <datalist id={`unified-origin-options-${index}`}>
                    {locations
                      .sort((a, b) => a.location_name.localeCompare(b.location_name))
                      .map(loc => <option key={loc.location_name} value={loc.location_name} />)}
                  </datalist>
                  <input
                    list={`unified-destination-options-${index}`}
                    placeholder="Type destination..."
                    value={trip.destination}
                    onChange={(e) => updateTrip(index, 'destination', e.target.value)}
                    style={{ width: '100%', marginBottom: 4, padding: '6px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}
                  />
                  <datalist id={`unified-destination-options-${index}`}>
                    {locations
                      .sort((a, b) => a.location_name.localeCompare(b.location_name))
                      .map(loc => <option key={loc.location_name} value={loc.location_name} />)}
                  </datalist>
                  <InputNumber
                    size="small"
                    placeholder="Price"
                    value={trip.price}
                    onChange={(value) => updateTrip(index, 'price', value)}
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                  />
                </Card>
              ))}
              {editingTrips.length > 0 && (
                <Button danger block size="small" onClick={clearTrips} style={{ marginTop: 8 }}>Clear All Trips</Button>
              )}
            </Card>
          </Col>

          <Col span={8}>
            <Card 
              title={`Fuel (${editingFuel.length})`} 
              size="small"
              extra={
                <Space>
                  <Button size="small" icon={<PlusOutlined />} onClick={addFuel}>Add</Button>
                  <Button size="small" type="primary" onClick={saveFuel} loading={loading}>Save</Button>
                </Space>
              }
            >
              {editingFuel.map((fuel, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span>Fuel {index + 1}</span>
                    <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeFuel(index)} />
                  </div>
                  <InputNumber
                    size="small"
                    placeholder="Amount ($)"
                    value={fuel.amount_spent}
                    onChange={(value) => updateFuel(index, 'amount_spent', value)}
                    style={{ width: '100%', marginBottom: 4 }}
                    min={0}
                    step={0.01}
                  />
                  <InputNumber
                    size="small"
                    placeholder="Liters"
                    value={fuel.liters}
                    onChange={(value) => updateFuel(index, 'liters', value)}
                    style={{ width: '100%', marginBottom: 4 }}
                    min={0}
                    step={0.1}
                  />
                  <InputNumber
                    size="small"
                    placeholder="Odometer"
                    value={fuel.odometer}
                    onChange={(value) => updateFuel(index, 'odometer', value)}
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Card>
              ))}
              {editingFuel.length > 0 && (
                <Button danger block size="small" onClick={clearFuel} style={{ marginTop: 8 }}>Clear All Fuel</Button>
              )}
            </Card>
          </Col>

          <Col span={8}>
            <Card 
              title={`Expenses (${editingExpenses.length})`} 
              size="small"
              extra={
                <Space>
                  <Button size="small" icon={<PlusOutlined />} onClick={addExpense}>Add</Button>
                  <Button size="small" type="primary" onClick={saveExpenses} loading={loading}>Save</Button>
                </Space>
              }
            >
              {editingExpenses.map((expense, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span>Expense {index + 1}</span>
                    <Button size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeExpense(index)} />
                  </div>
                  <Select
                    size="small"
                    placeholder="Type"
                    value={expense.expense_type}
                    onChange={(value) => updateExpense(index, 'expense_type', value)}
                    style={{ width: '100%', marginBottom: 4 }}
                  >
                    <Select.Option value="police">Police Fine</Select.Option>
                    <Select.Option value="vid">VID</Select.Option>
                    <Select.Option value="repairs">Repairs</Select.Option>
                    <Select.Option value="other">Other</Select.Option>
                  </Select>
                  <InputNumber
                    size="small"
                    placeholder="Amount ($)"
                    value={expense.amount}
                    onChange={(value) => updateExpense(index, 'amount', value)}
                    style={{ width: '100%', marginBottom: 4 }}
                    min={0}
                    step={0.01}
                  />
                  <Input.TextArea
                    size="small"
                    placeholder="Description"
                    value={expense.description}
                    onChange={(e) => updateExpense(index, 'description', e.target.value)}
                    rows={2}
                  />
                </Card>
              ))}
              {editingExpenses.length > 0 && (
                <Button danger block size="small" onClick={clearExpenses} style={{ marginTop: 8 }}>Clear All Expenses</Button>
              )}
            </Card>
          </Col>
          </Row>
        </>
      )}
      
      <Modal
        title="Add New Location"
        open={locationModalVisible}
        onOk={addLocation}
        onCancel={() => {
          setLocationModalVisible(false);
          setNewLocation('');
        }}
        okText="Add Location"
      >
        <Input 
          placeholder="Enter location name" 
          value={newLocation}
          onChange={(e) => setNewLocation(e.target.value)}
          onPressEnter={addLocation}
          autoFocus
        />
      </Modal>

      <Modal
        title="Temporary Truck Assignment"
        open={assignmentModalVisible}
        onCancel={() => setAssignmentModalVisible(false)}
        footer={null}
      >
        <Form onFinish={saveAssignment} initialValues={{ truck_id: tempAssignment?.truck_id }}>
          <Form.Item name="truck_id" label="Truck" rules={[{ required: true, message: 'Please select truck' }]}>
            <Select placeholder="Select truck">
              {trucks.map(truck => (
                <Select.Option key={truck.truck_id} value={truck.truck_id}>
                  {truck.license_plate}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input placeholder="Optional reason" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Save</Button>
            {tempAssignment && <Button danger onClick={deleteAssignment}>Delete</Button>}
            <Button onClick={() => setAssignmentModalVisible(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default UnifiedActions;